/**
 * Authentication Context
 * Provee estado global de autenticación a toda la aplicación
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import AuthService from '../services/AuthService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Verificar si hay una sesión activa al cargar la app
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = AuthService.getToken();
                if (token) {
                    // Verificar que el token sea válido
                    const userData = await AuthService.verifyToken();
                    setUser(userData);
                    setIsAuthenticated(true);
                }
            } catch (error) {
                console.error('Auth check failed:', error);

                // Solo limpiar sesión si es un error de autenticación (401) o token inválido
                if (error.status === 401 || error.message === 'No token found') {
                    AuthService.removeToken();
                    AuthService.removeUser();
                    setUser(null);
                    setIsAuthenticated(false);
                } else {
                    // Si es otro error (500, network), mantenemos el estado "intentando" o asumimos offline
                    // Para no molestar al usuario, si ya teníamos user en localStorage, podríamos mantenerlo?
                    // Por seguridad, si falla la verificación, mejor no dar por autenticado, PERO no borrar el token.
                    // Así el próximo F5 podría funcionar si el server vuelve.
                    console.warn("Server unavailable or error, keeping token for retry.");
                }
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, []);

    /**
     * Registrar nuevo usuario
     */
    const register = async (email, password, name, website = '') => {
        try {
            const data = await AuthService.register(email, password, name, website);
            // Después de registrar, hacer login automáticamente
            await login(email, password);
            return data;
        } catch (error) {
            throw error;
        }
    };

    /**
     * Iniciar sesión
     */
    const login = async (email, password) => {
        try {
            const data = await AuthService.login(email, password);
            setUser(data.user);
            setIsAuthenticated(true);
            return data;
        } catch (error) {
            throw error;
        }
    };

    /**
     * Cerrar sesión
     */
    const logout = async () => {
        try {
            await AuthService.logout();
            setUser(null);
            setIsAuthenticated(false);
        } catch (error) {
            console.error('Logout error:', error);
            // Limpiar de todos modos
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    /**
     * Actualizar datos del usuario
     */
    const updateUser = (userData) => {
        setUser(userData);
        AuthService.setUser(userData);
    };

    /**
     * Verificar si el usuario es admin
     */
    const isAdmin = () => {
        return user && user.role && user.role.toLowerCase() === 'admin';
    };

    const value = {
        user,
        token: AuthService.getToken(),
        loading,
        isAuthenticated,
        register,
        login,
        logout,
        updateUser,
        isAdmin,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
// Revisado: 2025-12-16
